/**
 * The settings scope axis: which project and which environment a settings
 * page is currently looking at.
 *
 * Adapted from upstream's `settingsScope.ts` (pingdotgg/t3code, MIT, pin
 * e3e7cc3f). Upstream resolves three axes there: project, machine, and
 * checkout. Forge's shell wraps two, global/project/environment per the
 * parity plan; a checkout axis would need per-row scoped settings to be
 * worth navigating to, so it lands with the ticket that scopes real rows.
 *
 * The resolver is pure and structural on purpose: the React layer feeds it
 * forge's project groups and environment list, and this file stays testable
 * without a store or a router.
 *
 * @module
 */

export interface SettingsScopeSearch {
  readonly project?: string | undefined;
  readonly machine?: string | undefined;
}

/** One project group, as the scope menus and the resolver see it. */
export interface SettingsScopeGroup {
  readonly projectKey: string;
  readonly displayName: string;
  /** Every environment the group has a checkout on, deduplicated. */
  readonly environmentIds: readonly string[];
}

export interface SettingsScopeEnvironment {
  readonly environmentId: string;
  readonly label: string;
}

type ScopeBase = {
  readonly label: string;
  readonly environmentIds: readonly string[];
};

/**
 * What the selected axes point at. Stale ids stay visible to the resolver so
 * a removed target reads as unavailable, never as a silent "all".
 */
export type ResolvedSettingsScope = ScopeBase &
  (
    | { readonly kind: "all" }
    | {
        readonly kind: "environment";
        readonly environmentId: string;
      }
    | {
        readonly kind: "project";
        readonly group: SettingsScopeGroup;
        /** The environment the scope is narrowed to, or null for all of them. */
        readonly environmentId: string | null;
      }
    | {
        readonly kind: "unavailable";
        readonly reason: "project-missing" | "environment-missing";
        readonly message: string;
      }
  );

/** A non-empty URL string, or nothing. */
function nonEmptyString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim().length > 0 ? value : undefined;
}

/** Accepts anything a URL can carry; only the two axes survive. */
export function validateSettingsScopeSearch(raw: Record<string, unknown>): SettingsScopeSearch {
  const project = nonEmptyString(raw.project);
  const machine = nonEmptyString(raw.machine);
  return {
    ...(project === undefined ? {} : { project }),
    ...(machine === undefined ? {} : { machine }),
  };
}

type UnavailableReason = Extract<ResolvedSettingsScope, { kind: "unavailable" }>["reason"];

function unavailableScope(reason: UnavailableReason, message: string): ResolvedSettingsScope {
  return {
    kind: "unavailable",
    reason,
    label: "Unavailable selection",
    message,
    environmentIds: [],
  };
}

/** Resolves only existing targets. An unavailable selection never broadens a later write. */
export function resolveSettingsScope(
  search: SettingsScopeSearch,
  groups: readonly SettingsScopeGroup[],
  environments: readonly SettingsScopeEnvironment[],
): ResolvedSettingsScope {
  const environment = environments.find((candidate) => candidate.environmentId === search.machine);
  if (search.machine && !environment) {
    return unavailableScope("environment-missing", "This environment is no longer available.");
  }

  if (search.project) {
    const group = groups.find((candidate) => candidate.projectKey === search.project);
    if (!group) return unavailableScope("project-missing", "This project is no longer available.");

    // An environment the project has no checkout on cannot serve as its
    // narrow: the axes are only meaningful together.
    const environmentIds = search.machine === undefined ? group.environmentIds : [search.machine];
    if (search.machine !== undefined && !group.environmentIds.includes(search.machine)) {
      return unavailableScope(
        "environment-missing",
        "This project has no checkout on the selected environment.",
      );
    }
    return {
      kind: "project",
      group,
      environmentId: environment?.environmentId ?? null,
      label: `${group.displayName} / ${environment?.label ?? "All environments"}`,
      environmentIds,
    };
  }

  if (environment) {
    return {
      kind: "environment",
      environmentId: environment.environmentId,
      label: environment.label,
      environmentIds: [environment.environmentId],
    };
  }
  return {
    kind: "all",
    label: "All environments",
    environmentIds: environments.map((candidate) => candidate.environmentId),
  };
}
